// src/components/Footer.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Footer = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [emailCopied, setEmailCopied] = useState(false);
  
  const handleCopyEmail = () => {
    navigator.clipboard.writeText("isabellaibeji@outlook.com");
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };
  
  const currentYear = new Date().getFullYear();
  
  return (
    <FooterSection ref={ref}>
      <Container>
        <FooterContainer
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <FooterBrand>
            <FooterLogo className="gradient-text-primary">ISABELLA IBEJI</FooterLogo>
            <FooterTagline>Digital Advertising Strategist</FooterTagline>
          </FooterBrand>
          
          <FooterLinks>
            <FooterLinksGroup>
              <FooterLinksTitle>Navigate</FooterLinksTitle>
              <FooterLinksList>
                <FooterLinkItem>
                  <FooterLink href="#work">Work</FooterLink>
                </FooterLinkItem>
                <FooterLinkItem>
                  <FooterLink href="#expertise">Expertise</FooterLink>
                </FooterLinkItem>
                <FooterLinkItem>
                  <FooterLink href="#process">Process</FooterLink>
                </FooterLinkItem>
                <FooterLinkItem>
                  <FooterLink href="#about">About</FooterLink>
                </FooterLinkItem>
              </FooterLinksList>
            </FooterLinksGroup>
            
            <FooterLinksGroup>
              <FooterLinksTitle>Connect</FooterLinksTitle>
              <FooterLinksList>
                <FooterLinkItem>
                  <EmailDisplay>
                    <span>isabellaibeji [at] outlook [dot] com</span>
                    <CopyButton onClick={handleCopyEmail}>
                      {emailCopied ? "Copied!" : "Copy"}
                    </CopyButton>
                  </EmailDisplay>
                </FooterLinkItem>
                <FooterLinkItem>
                  <FooterLink href="https://linkedin.com/in/isabellaibeji" target="_blank">LinkedIn</FooterLink>
                </FooterLinkItem>
                <FooterLinkItem>
                  <FooterLink href="https://twitter.com/isabellaibeji" target="_blank">Twitter</FooterLink>
                </FooterLinkItem>
                <FooterLinkItem>
                  <FooterLink href="https://calendar.app.google/JNzzrs67cJGNb3Vq8" target="_blank">Schedule a Call</FooterLink>
                </FooterLinkItem>
              </FooterLinksList>
            </FooterLinksGroup>
          </FooterLinks>
          
          <FooterSocial>
            <SocialLink href="https://linkedin.com/in/isabellaibeji" target="_blank">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                <rect x="2" y="9" width="4" height="12"></rect>
                <circle cx="4" cy="4" r="2"></circle>
              </svg>
            </SocialLink>
            <SocialLink href="https://twitter.com/isabellaibeji" target="_blank">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
              </svg>
            </SocialLink>
            <SocialLink 
              href="#"
              onClick={handleCopyEmail}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
            </SocialLink>
            <SocialLink href="tel:+15551234567">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
            </SocialLink>
          </FooterSocial>
        </FooterContainer>
        
        <Copyright
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <CopyrightText>© {currentYear} Isabella Ibeji. All rights reserved.</CopyrightText>
        </Copyright>
      </Container>
    </FooterSection>
  );
};

const FooterSection = styled.footer`
  padding: 4rem 0 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 1.5rem;
  }
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const FooterBrand = styled.div``;

const FooterLogo = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 0.5rem;
`;

const FooterTagline = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9375rem;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const FooterLinksGroup = styled.div``;

const FooterLinksTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
`;

const FooterLinksList = styled.ul`
  list-style: none;
`;

const FooterLinkItem = styled.li`
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FooterLink = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9375rem;
  transition: color ${({ theme }) => theme.transitions.default};
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

// Email display with copy button
const EmailDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9375rem;
  
  span {
    transition: color ${({ theme }) => theme.transitions.default};
  }
  
  &:hover span {
    color: ${({ theme }) => theme.colors.text};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const CopyButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: ${({ theme }) => theme.colors.text};
  }
`;

const FooterSocial = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all ${({ theme }) => theme.transitions.default};
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: ${({ theme }) => theme.colors.text};
    transform: translateY(-2px);
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const CopyrightText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.875rem;
`;

export default Footer;